import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { theme } from '../../../theme';
import DashboardLayout from '../../../components/dashboard/DashboardLayout';
import { Card, CardBody } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Table } from '../../../components/ui/Table';
import { Modal } from '../../../components/ui/Modal';
import { ScrollableContainer } from '../../../components/ui/ScrollableContainer';
import { DataTable } from '../../../components/ui/DataTable';
import { useAuth } from '../../../contexts/AuthContext';
import toast from 'react-hot-toast';
import { FiPlus, FiTrash2, FiSend, FiRefreshCw, FiEye, FiEdit } from 'react-icons/fi';
import {
  fetchPettyCashExpenses,
  addPettyCashExpense,
  deletePettyCashExpense,
  submitPettyCashExpenses,
  updatePettyCashExpense,
  PettyCashExpense,
} from '../service/pettyCashService';

const StatusBadge = styled.span<{ status: string }>`
  display: inline-block;
  padding: ${theme.spacing[1]} ${theme.spacing[3]};
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.typography.fontSize.xs};
  font-weight: ${theme.typography.fontWeight.medium};
  background-color: ${({ status }) => {
    switch (status) {
      case 'approved':
        return `${theme.colors.accent.success}20`;
      case 'rejected':
        return `${theme.colors.accent.danger}20`;
      case 'checked':
        return `${theme.colors.accent.warning}20`;
      case 'pending':
        return `${theme.colors.primary[500]}20`;
      default:
        return `${theme.colors.light.border.medium}20`;
    }
  }};
  color: ${({ status }) => {
    switch (status) {
      case 'approved':
        return theme.colors.accent.success;
      case 'rejected':
        return theme.colors.accent.danger;
      case 'checked':
        return theme.colors.accent.warning;
      case 'pending':
        return theme.colors.primary[600];
      default:
        return theme.colors.light.text.secondary;
    }
  }};
`;

const TabsContainer = styled.div`
  margin-bottom: ${theme.spacing[6]};
`;

const TabsList = styled.div`
  display: flex;
  gap: ${theme.spacing[2]};
  border-bottom: 2px solid ${theme.colors.light.border.medium};
  margin-bottom: ${theme.spacing[4]};
`;

const Tab = styled.button<{ active?: boolean }>`
  padding: ${theme.spacing[3]} ${theme.spacing[6]};
  border: none;
  background: transparent;
  color: ${({ active }) =>
    active ? theme.colors.primary[600] : theme.colors.light.text.secondary};
  font-size: ${theme.typography.fontSize.base};
  font-weight: ${({ active }) =>
    active ? theme.typography.fontWeight.bold : theme.typography.fontWeight.medium};
  cursor: pointer;
  border-bottom: 3px solid
    ${({ active }) => (active ? theme.colors.primary[600] : 'transparent')};
  transition: all 0.2s ease;
  margin-bottom: -2px;

  &:hover {
    color: ${theme.colors.primary[600]};
  }
`;

const TabContent = styled.div`
  display: ${({ hidden }: { hidden?: boolean }) => (hidden ? 'none' : 'block')};
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing[6]};
  flex-wrap: wrap;
  gap: ${theme.spacing[4]};
`;

const PageTitle = styled.h1`
  font-size: ${theme.typography.fontSize['2xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.secondary[900]};
  margin: 0;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: ${theme.spacing[2]};
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${theme.spacing[4]};
  margin-bottom: ${theme.spacing[6]};
`;

const StatCard = styled(Card)`
  text-align: center;
`;

const StatValue = styled.div<{ color?: string }>`
  font-size: ${theme.typography.fontSize['2xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${({ color }) => color || theme.colors.primary[600]};
  margin-bottom: ${theme.spacing[1]};
`;

const StatLabel = styled.div`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.light.text.secondary};
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${theme.spacing[4]};

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;

const Label = styled.label`
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${theme.colors.light.text.primary};
`;

const Select = styled.select`
  width: 100%;
  padding: ${theme.spacing[3]};
  border: 1px solid ${theme.colors.light.border.medium};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSize.base};
  color: ${theme.colors.light.text.primary};
  background-color: ${theme.colors.white};
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}20;
  }
`;

const ActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing[2]};
  border: none;
  border-radius: ${theme.borderRadius.md};
  background-color: transparent;
  color: ${theme.colors.light.text.secondary};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${theme.colors.light.background.tertiary};
    color: ${theme.colors.accent.danger};
  }
`;

const AmountCell = styled.div`
  text-align: right;
  color: ${theme.colors.accent.danger};
  font-weight: ${theme.typography.fontWeight.medium};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${theme.spacing[8]};
  color: ${theme.colors.light.text.secondary};
`;

const ViewButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing[2]};
  border: none;
  border-radius: ${theme.borderRadius.md};
  background-color: transparent;
  color: ${theme.colors.primary[600]};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${theme.colors.primary[50]};
    color: ${theme.colors.primary[700]};
  }
`;

const SubmissionRow = styled.tr`
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${theme.colors.primary[50]} !important;
  }
`;

const SubmissionSection = styled.div`
  margin-bottom: ${theme.spacing[8]};
  border: 1px solid ${theme.colors.light.border.medium};
  border-radius: ${theme.borderRadius.lg};
  overflow: hidden;
  background-color: ${theme.colors.white};
  box-shadow: ${theme.shadows.sm};
`;

const SectionHeader = styled.div`
  background-color: ${theme.colors.light.background.secondary};
  padding: ${theme.spacing[4]} ${theme.spacing[6]};
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid ${theme.colors.light.border.medium};

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: ${theme.spacing[3]};
  }
`;

const SectionInfo = styled.div`
  display: flex;
  gap: ${theme.spacing[6]};
  align-items: center;

  @media (max-width: 768px) {
    flex-wrap: wrap;
    gap: ${theme.spacing[3]};
  }
`;

const SectionTitle = styled.h3`
  font-size: ${theme.typography.fontSize.lg};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.secondary[900]};
  margin: 0;
`;

const SectionStat = styled.div`
  display: flex;
  flex-direction: column;
`;

const SectionStatLabel = styled.span`
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.light.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.025em;
`;

const SectionStatValue = styled.span<{ color?: string }>`
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${({ color }) => color || theme.colors.light.text.primary};
`;

interface SubmissionGroup {
  id: string;
  submittedAt: Date | any;
  submittedBy: string;
  submissionPeriod: 'daily' | 'weekly' | 'monthly';
  expenses: PettyCashExpense[];
  totalAmount: number;
  status: 'pending' | 'checked' | 'approved' | 'rejected';
}

const PettyCashBookPage: React.FC = () => {
  const { user, companyData, staffData } = useAuth();
  const companyId = companyData?.id || user?.uid || '';
  
  // Check if user is a company owner/admin (logged in from companies, not staff)
  const userEmail = user?.email?.toLowerCase();
  const isCompanyOwner = !!companyData && (
    !staffData || // No staffData = pure company owner
    (userEmail && (
      companyData.email?.toLowerCase() === userEmail || 
      companyData.emails?.some((email: string) => email.toLowerCase() === userEmail)
    )) // Email matches company = company owner
  );

  // Check permissions for petty cash
  const isStaffUser = !!staffData && !isCompanyOwner;
  const canEditPettyCash = isCompanyOwner || (isStaffUser && staffData?.permissions?.pettyCash === 'edit');

  const [draftExpenses, setDraftExpenses] = useState<PettyCashExpense[]>([]);
  const [submittedExpenses, setSubmittedExpenses] = useState<PettyCashExpense[]>([]);
  const [checkedExpenses, setCheckedExpenses] = useState<PettyCashExpense[]>([]);
  const [loading, setLoading] = useState(false);
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [selectedExpenseIds, setSelectedExpenseIds] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<'not-submitted' | 'submitted' | 'checked'>('not-submitted');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [showSubmissionDetailModal, setShowSubmissionDetailModal] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState<SubmissionGroup | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [expenseToEdit, setExpenseToEdit] = useState<PettyCashExpense | null>(null);
  const [editFormData, setEditFormData] = useState({
    date: '',
    amount: '',
    category: '',
    description: '',
    vendor: '',
    paymentMethod: 'cash',
    ref: '',
  });
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    amount: '',
    category: '',
    description: '',
    vendor: '',
    paymentMethod: 'cash',
    ref: '',
  });

  useEffect(() => {
    if (companyId) {
      loadExpenses();
    }
  }, [companyId]);

  const loadExpenses = async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      // Load draft expenses (not submitted)
      const draftData = await fetchPettyCashExpenses(companyId, 'draft');
      setDraftExpenses(draftData);

      // Load checked expenses separately
      try {
        const checkedData = await fetchPettyCashExpenses(companyId, 'checked');
        setCheckedExpenses(checkedData);
      } catch (error: any) {
        console.error('Error loading checked expenses:', error);
        if (error.code === 'failed-precondition' || 
            error.message?.includes('index') || 
            error.message?.includes('The query requires an index')) {
          console.error('⚠️ Firestore Index Error Detected!');
          console.error('https://console.firebase.google.com/v1/r/project/guard-crm/firestore/indexes');
        }
      }

      // Load all submitted expenses (pending, approved, rejected) - excluding checked
      const allSubmitted: PettyCashExpense[] = [];
      const statuses: Array<'pending' | 'approved' | 'rejected'> = ['pending', 'approved', 'rejected'];
      
      for (const status of statuses) {
        try {
          const statusData = await fetchPettyCashExpenses(companyId, status);
          allSubmitted.push(...statusData);
        } catch (error: any) {
          // Log full error to console
          console.error(`Error loading ${status} expenses:`, error);
          console.error('Error code:', error.code);
          console.error('Error message:', error.message);
          console.error('Full error object:', JSON.stringify(error, null, 2));
          
          // Check if it's an index error
          if (error.code === 'failed-precondition' || 
              error.message?.includes('index') || 
              error.message?.includes('The query requires an index')) {
            console.error('⚠️ Firestore Index Error Detected!');
            console.error('This query requires a composite index. Please create the index at:');
            console.error('https://console.firebase.google.com/v1/r/project/guard-crm/firestore/indexes');
          }
        }
      }
      
      setSubmittedExpenses(allSubmitted);
    } catch (error: any) {
      // Log full error to console
      console.error('Error loading petty cash expenses:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      console.error('Full error object:', JSON.stringify(error, null, 2));
      
      // Check if it's an index error
      if (error.code === 'failed-precondition' || 
          error.message?.includes('index') || 
          error.message?.includes('The query requires an index')) {
        console.error('⚠️ Firestore Index Error Detected!');
        console.error('This query requires a composite index. Please create the index at:');
        console.error('https://console.firebase.google.com/v1/r/project/guard-crm/firestore/indexes');
        toast.error('Database index required. Please check the console for details.');
      } else {
        toast.error(error.message || 'Failed to load expenses');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      await addPettyCashExpense(companyId, {
        date: new Date(formData.date),
        amount: parseFloat(formData.amount),
        category: formData.category,
        description: formData.description,
        vendor: formData.vendor || undefined,
        paymentMethod: formData.paymentMethod || undefined,
        ref: formData.ref || undefined,
      });

      toast.success('Expense added successfully');
      setShowExpenseModal(false);
      setFormData({
        date: new Date().toISOString().split('T')[0],
        amount: '',
        category: '',
        description: '',
        vendor: '',
        paymentMethod: 'cash',
        ref: '',
      });
      await loadExpenses();
    } catch (error: any) {
      toast.error(error.message || 'Failed to add expense');
    } finally {
      setSaving(false);
    }
  };

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [expenseToDelete, setExpenseToDelete] = useState<string | null>(null);

  const handleDelete = async (expenseId: string) => {
    setExpenseToDelete(expenseId);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!expenseToDelete) return;

    try {
      await deletePettyCashExpense(companyId, expenseToDelete);
      toast.success('Expense deleted successfully');
      setShowDeleteModal(false);
      setExpenseToDelete(null);
      await loadExpenses();
    } catch (error: any) {
      toast.error(error.message || 'Failed to delete expense');
    }
  };

  const handleToggleSelect = (expenseId: string) => {
    setSelectedExpenseIds((prev) =>
      prev.includes(expenseId)
        ? prev.filter((id) => id !== expenseId)
        : [...prev, expenseId]
    );
  };

  const handleSelectAll = () => {
    if (activeTab === 'not-submitted') {
      if (selectedExpenseIds.length === draftExpenses.length) {
        setSelectedExpenseIds([]);
      } else {
        setSelectedExpenseIds(draftExpenses.map((e) => e.id));
      }
    } else if (activeTab === 'checked') {
      if (selectedExpenseIds.length === checkedExpenses.length) {
        setSelectedExpenseIds([]);
      } else {
        setSelectedExpenseIds(checkedExpenses.map((e) => e.id));
      }
    }
  };

  const handleSelectGroup = (expenseIds: string[]) => {
    const allSelected = expenseIds.every(id => selectedExpenseIds.includes(id));
    if (allSelected) {
      setSelectedExpenseIds(prev => prev.filter(id => !expenseIds.includes(id)));
    } else {
      // Use Array.from(new Set(...)) to avoid TS2802 iteration error
      setSelectedExpenseIds(prev => Array.from(new Set([...prev, ...expenseIds])));
    }
  };

  const handleSubmitForReview = async () => {
    if (selectedExpenseIds.length === 0) {
      toast.error('Please select at least one expense to submit');
      return;
    }

    setSaving(true);
    try {
      // Submit directly with default 'daily' period
      await submitPettyCashExpenses(companyId, selectedExpenseIds, 'daily');
      toast.success(`Successfully submitted ${selectedExpenseIds.length} expense(s) for review`);
      setSelectedExpenseIds([]);
      await loadExpenses();
    } catch (error: any) {
      toast.error(error.message || 'Failed to submit expenses');
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (date: Date | any): string => {
    if (!date) return '—';
    const d = date instanceof Date ? date : date.toDate ? date.toDate() : new Date(date);
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const draftTotalAmount = draftExpenses.reduce((sum, e) => sum + e.amount, 0);
  const submittedTotalAmount = submittedExpenses.reduce((sum, e) => sum + e.amount, 0);
  const selectedAmount = draftExpenses
    .filter((e) => selectedExpenseIds.includes(e.id))
    .reduce((sum, e) => sum + e.amount, 0);

  // Group submitted expenses by submission
  const groupExpensesBySubmission = (expenses: PettyCashExpense[]): SubmissionGroup[] => {
    const groups = new Map<string, PettyCashExpense[]>();

    expenses.forEach((expense) => {
      if (!expense.submittedAt) return;

      // Create a unique key based on submittedAt timestamp (rounded to second), submittedBy, and submissionPeriod
      let submittedAt: Date;
      if (expense.submittedAt instanceof Date) {
        submittedAt = expense.submittedAt;
      } else if (expense.submittedAt && typeof (expense.submittedAt as any).toDate === 'function') {
        submittedAt = (expense.submittedAt as any).toDate();
      } else if (expense.submittedAt && typeof expense.submittedAt === 'object' && (expense.submittedAt as any).seconds) {
        submittedAt = new Date((expense.submittedAt as any).seconds * 1000);
      } else {
        submittedAt = new Date(expense.submittedAt as any);
      }
      
      // Round to nearest second for grouping
      const timestamp = Math.floor(submittedAt.getTime() / 1000) * 1000;
      const key = `${timestamp}_${expense.submittedBy || 'unknown'}_${expense.submissionPeriod || 'daily'}`;
      
      if (!groups.has(key)) {
        groups.set(key, []);
      }
      groups.get(key)!.push(expense);
    });

    return Array.from(groups.entries()).map(([key, expenses]) => {
      const firstExpense = expenses[0];
      let submittedAt: Date;
      if (firstExpense.submittedAt instanceof Date) {
        submittedAt = firstExpense.submittedAt;
      } else if (firstExpense.submittedAt && typeof (firstExpense.submittedAt as any).toDate === 'function') {
        submittedAt = (firstExpense.submittedAt as any).toDate();
      } else if (firstExpense.submittedAt && typeof firstExpense.submittedAt === 'object' && (firstExpense.submittedAt as any).seconds) {
        submittedAt = new Date((firstExpense.submittedAt as any).seconds * 1000);
      } else {
        submittedAt = new Date(firstExpense.submittedAt as any);
      }
      
      // Determine overall status (if all same, use that; otherwise use most common)
      const statuses = expenses.map(e => e.submissionStatus);
      const statusCounts = statuses.reduce((acc, status) => {
        acc[status] = (acc[status] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);
      const mostCommonStatus = Object.entries(statusCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || 'pending';

      return {
        id: key,
        submittedAt,
        submittedBy: firstExpense.submittedBy || 'unknown',
        submissionPeriod: firstExpense.submissionPeriod || 'daily',
        expenses: expenses.sort((a, b) => {
          let dateA: Date;
          if (a.date instanceof Date) {
            dateA = a.date;
          } else if (a.date && typeof (a.date as any).toDate === 'function') {
            dateA = (a.date as any).toDate();
          } else if (a.date && typeof a.date === 'object' && (a.date as any).seconds) {
            dateA = new Date((a.date as any).seconds * 1000);
          } else {
            dateA = new Date(a.date as any);
          }

          let dateB: Date;
          if (b.date instanceof Date) {
            dateB = b.date;
          } else if (b.date && typeof (b.date as any).toDate === 'function') {
            dateB = (b.date as any).toDate();
          } else if (b.date && typeof b.date === 'object' && (b.date as any).seconds) {
            dateB = new Date((b.date as any).seconds * 1000);
          } else {
            dateB = new Date(b.date as any);
          }

          return dateB.getTime() - dateA.getTime();
        }),
        totalAmount: expenses.reduce((sum, e) => sum + e.amount, 0),
        status: mostCommonStatus as 'pending' | 'checked' | 'approved' | 'rejected',
      };
    }).sort((a, b) => {
      const dateA = a.submittedAt instanceof Date ? a.submittedAt : new Date(a.submittedAt);
      const dateB = b.submittedAt instanceof Date ? b.submittedAt : new Date(b.submittedAt);
      return dateB.getTime() - dateA.getTime();
    });
  };

  const submissionGroups = groupExpensesBySubmission(submittedExpenses);
  const checkedSubmissionGroups = groupExpensesBySubmission(checkedExpenses);

  // Pagination for submission groups
  const totalPages = activeTab === 'submitted' 
    ? Math.ceil(submissionGroups.length / itemsPerPage)
    : Math.ceil(checkedSubmissionGroups.length / itemsPerPage);
    
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  
  const paginatedSubmissions = submissionGroups.slice(startIndex, endIndex);
  const paginatedCheckedSubmissions = checkedSubmissionGroups.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleViewSubmission = (submission: SubmissionGroup) => {
    setSelectedSubmission(submission);
    setShowSubmissionDetailModal(true);
  };

  const handleEditCheckedExpense = (expense: PettyCashExpense) => {
    let expenseDate: Date;
    if (expense.date instanceof Date) {
      expenseDate = expense.date;
    } else if (expense.date && typeof (expense.date as any).toDate === 'function') {
      expenseDate = (expense.date as any).toDate();
    } else if (expense.date && typeof expense.date === 'object' && (expense.date as any).seconds) {
      expenseDate = new Date((expense.date as any).seconds * 1000);
    } else {
      expenseDate = new Date(expense.date as any);
    }
    
    setEditFormData({
      date: expenseDate.toISOString().split('T')[0],
      amount: expense.amount.toString(),
      category: expense.category,
      description: expense.description,
      vendor: expense.vendor || '',
      paymentMethod: expense.paymentMethod || 'cash',
      ref: expense.ref || '',
    });
    setExpenseToEdit(expense);
    setShowEditModal(true);
  };

  const handleUpdateCheckedExpense = async () => {
    if (!expenseToEdit) return;

    setSaving(true);
    try {
      await updatePettyCashExpense(companyId, expenseToEdit.id, {
        date: new Date(editFormData.date),
        amount: parseFloat(editFormData.amount),
        category: editFormData.category,
        description: editFormData.description,
        vendor: editFormData.vendor || undefined,
        paymentMethod: editFormData.paymentMethod || undefined,
        ref: editFormData.ref || undefined,
        submissionStatus: 'checked', // Keep as checked after edit
      });
      toast.success('Expense updated successfully');
      setShowEditModal(false);
      setExpenseToEdit(null);
      await loadExpenses();
    } catch (error: any) {
      toast.error(error.message || 'Failed to update expense');
    } finally {
      setSaving(false);
    }
  };

  const handleResubmitChecked = async (expenseId: string) => {
    setSaving(true);
    try {
      await submitPettyCashExpenses(companyId, [expenseId], 'daily');
      toast.success('Expense resubmitted successfully');
      await loadExpenses();
    } catch (error: any) {
      toast.error(error.message || 'Failed to resubmit expense');
    } finally {
      setSaving(false);
    }
  };

  const expenseCategories = [
    'Office Supplies',
    'Transportation',
    'Meals & Entertainment',
    'Utilities',
    'Maintenance',
    'Communication',
    'Miscellaneous',
  ];

  return (
    <DashboardLayout>
      <ScrollableContainer>
        <PageHeader>
          <PageTitle>Petty Cash Book</PageTitle>
          <ActionButtons>
            {canEditPettyCash && (
              <>
                <Button
                  onClick={() => setShowExpenseModal(true)}
                  disabled={loading}
                >
                  <FiPlus size={18} style={{ marginRight: theme.spacing[2] }} />
                  Add Expense
                </Button>
                <Button
                  onClick={handleSubmitForReview}
                  disabled={loading || selectedExpenseIds.length === 0 || saving}
                  variant="primary"
                  loading={saving}
                >
                  <FiSend size={18} style={{ marginRight: theme.spacing[2] }} />
                  Submit for Review ({selectedExpenseIds.length})
                </Button>
              </>
            )}
            <Button onClick={loadExpenses} disabled={loading}>
              <FiRefreshCw size={18} style={{ marginRight: theme.spacing[2] }} />
              Refresh
            </Button>
          </ActionButtons>
        </PageHeader>

        <StatsContainer>
          <StatCard>
            <CardBody>
              <StatValue color={theme.colors.accent.danger}>
                {formatCurrency(draftTotalAmount)}
              </StatValue>
              <StatLabel>Total Draft Expenses</StatLabel>
            </CardBody>
          </StatCard>
          <StatCard>
            <CardBody>
              <StatValue>{draftExpenses.length}</StatValue>
              <StatLabel>Draft Entries</StatLabel>
            </CardBody>
          </StatCard>
          <StatCard>
            <CardBody>
              <StatValue color={theme.colors.primary[600]}>
                {formatCurrency(selectedAmount)}
              </StatValue>
              <StatLabel>Selected Amount</StatLabel>
            </CardBody>
          </StatCard>
          <StatCard>
            <CardBody>
              <StatValue color={theme.colors.accent.warning}>
                {formatCurrency(submittedTotalAmount)}
              </StatValue>
              <StatLabel>Submitted Expenses</StatLabel>
            </CardBody>
          </StatCard>
        </StatsContainer>

        {/* Tabs */}
        <TabsContainer>
          <TabsList>
            <Tab
              active={activeTab === 'not-submitted'}
              onClick={() => {
                setActiveTab('not-submitted');
                setSelectedExpenseIds([]);
              }}
            >
              Not Submitted ({draftExpenses.length})
            </Tab>
            <Tab
              active={activeTab === 'submitted'}
              onClick={() => {
                setActiveTab('submitted');
                setSelectedExpenseIds([]);
                setCurrentPage(1);
              }}
            >
              Submitted ({submissionGroups.length})
            </Tab>
            <Tab
              active={activeTab === 'checked'}
              onClick={() => {
                setActiveTab('checked');
                setSelectedExpenseIds([]);
                setCurrentPage(1);
              }}
            >
              Checked ({checkedExpenses.length})
            </Tab>
          </TabsList>

          {/* Not Submitted Tab */}
          <TabContent hidden={activeTab !== 'not-submitted'}>
            <Card>
              <CardBody>
                {loading ? (
                  <div style={{ textAlign: 'center', padding: theme.spacing[8] }}>
                    Loading expenses...
                  </div>
                ) : draftExpenses.length > 0 ? (
                  <>
                    <div style={{ marginBottom: theme.spacing[4] }}>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={handleSelectAll}
                      >
                        {selectedExpenseIds.length === draftExpenses.length ? 'Deselect All' : 'Select All'}
                      </Button>
                    </div>
                    <Table>
                      <Table.Header>
                        <Table.Row>
                          <Table.HeaderCell style={{ width: '50px' }}>
                            <input
                              type="checkbox"
                              checked={selectedExpenseIds.length === draftExpenses.length && draftExpenses.length > 0}
                              onChange={handleSelectAll}
                            />
                          </Table.HeaderCell>
                          <Table.HeaderCell>Date</Table.HeaderCell>
                          <Table.HeaderCell>Category</Table.HeaderCell>
                          <Table.HeaderCell>Description</Table.HeaderCell>
                          <Table.HeaderCell>Vendor</Table.HeaderCell>
                          <Table.HeaderCell>Payment Method</Table.HeaderCell>
                          <Table.HeaderCell style={{ textAlign: 'right' }}>Amount</Table.HeaderCell>
                          {canEditPettyCash && <Table.HeaderCell>Actions</Table.HeaderCell>}
                        </Table.Row>
                      </Table.Header>
                      <Table.Body>
                        {draftExpenses.map((expense) => (
                          <Table.Row key={expense.id}>
                            <Table.Cell>
                              <input
                                type="checkbox"
                                checked={selectedExpenseIds.includes(expense.id)}
                                onChange={() => handleToggleSelect(expense.id)}
                              />
                            </Table.Cell>
                            <Table.Cell>{formatDate(expense.date)}</Table.Cell>
                            <Table.Cell>{expense.category || '—'}</Table.Cell>
                            <Table.Cell>{expense.description || '—'}</Table.Cell>
                            <Table.Cell>{expense.vendor || '—'}</Table.Cell>
                            <Table.Cell>{expense.paymentMethod || '—'}</Table.Cell>
                            <Table.Cell style={{ textAlign: 'right' }}>
                              <AmountCell>{formatCurrency(expense.amount)}</AmountCell>
                            </Table.Cell>
                            {canEditPettyCash && (
                              <Table.Cell>
                                <ActionButton
                                  onClick={() => handleDelete(expense.id)}
                                  title="Delete"
                                >
                                  <FiTrash2 size={18} />
                                </ActionButton>
                              </Table.Cell>
                            )}
                          </Table.Row>
                        ))}
                      </Table.Body>
                    </Table>
                  </>
                ) : (
                  <EmptyState>
                    <p>No draft expenses. Add your first expense to get started.</p>
                  </EmptyState>
                )}
              </CardBody>
            </Card>
          </TabContent>

          {/* Submitted Tab */}
          <TabContent hidden={activeTab !== 'submitted'}>
            <Card>
              <CardBody>
                {loading ? (
                  <div style={{ textAlign: 'center', padding: theme.spacing[8] }}>
                    Loading expenses...
                  </div>
                ) : submissionGroups.length > 0 ? (
                  <DataTable
                    header={
                      <tr>
                        <th>Submitted Date</th>
                        <th>Submission Period</th>
                        <th>Number of Expenses</th>
                        <th style={{ textAlign: 'right' }}>Total Amount</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    }
                    body={
                      <>
                        {paginatedSubmissions.map((submission) => (
                          <SubmissionRow
                            key={submission.id}
                            onClick={() => handleViewSubmission(submission)}
                          >
                            <td>{formatDate(submission.submittedAt)}</td>
                            <td>
                              {submission.submissionPeriod
                                ? submission.submissionPeriod.charAt(0).toUpperCase() + submission.submissionPeriod.slice(1)
                                : '—'}
                            </td>
                            <td>{submission.expenses.length}</td>
                            <td style={{ textAlign: 'right' }}>
                              <AmountCell>{formatCurrency(submission.totalAmount)}</AmountCell>
                            </td>
                            <td>
                              <StatusBadge status={submission.status}>
                                {submission.status.charAt(0).toUpperCase() + submission.status.slice(1)}
                              </StatusBadge>
                            </td>
                            <td>
                              <ViewButton
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleViewSubmission(submission);
                                }}
                                title="View Details"
                              >
                                <FiEye size={18} />
                              </ViewButton>
                            </td>
                          </SubmissionRow>
                        ))}
                      </>
                    }
                    pagination={{
                      currentPage,
                      totalPages,
                      totalItems: submissionGroups.length,
                      pageSize: itemsPerPage,
                      onPageChange: handlePageChange,
                      loading,
                    }}
                  />
                ) : (
                  <EmptyState>
                    <p>No submitted expenses yet.</p>
                  </EmptyState>
                )}
              </CardBody>
            </Card>
          </TabContent>

          {/* Checked Tab */}
          <TabContent hidden={activeTab !== 'checked'}>
            {loading ? (
              <Card>
                <CardBody>
                  <div style={{ textAlign: 'center', padding: theme.spacing[8] }}>
                    Loading expenses...
                  </div>
                </CardBody>
              </Card>
            ) : checkedSubmissionGroups.length > 0 ? (
              <>
                <div style={{ marginBottom: theme.spacing[4], display: 'flex', gap: theme.spacing[2] }}>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={handleSelectAll}
                  >
                    {selectedExpenseIds.length === checkedExpenses.length ? 'Deselect All' : 'Select All'}
                  </Button>
                </div>

                {paginatedCheckedSubmissions.map((group) => {
                  const groupExpenseIds = group.expenses.map(e => e.id);
                  const isGroupAllSelected = groupExpenseIds.every(id => selectedExpenseIds.includes(id));
                  
                  return (
                    <SubmissionSection key={group.id}>
                      <SectionHeader>
                        <SectionInfo>
                          <input 
                            type="checkbox"
                            checked={isGroupAllSelected}
                            onChange={() => handleSelectGroup(groupExpenseIds)}
                            style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                          />
                          <SectionTitle>Submission: {formatDate(group.submittedAt)}</SectionTitle>
                          <SectionStat>
                            <SectionStatLabel>Period</SectionStatLabel>
                            <SectionStatValue>
                              {group.submissionPeriod ? group.submissionPeriod.charAt(0).toUpperCase() + group.submissionPeriod.slice(1) : '—'}
                            </SectionStatValue>
                          </SectionStat>
                          <SectionStat>
                            <SectionStatLabel>Items</SectionStatLabel>
                            <SectionStatValue>{group.expenses.length}</SectionStatValue>
                          </SectionStat>
                          <SectionStat>
                            <SectionStatLabel>Total Amount</SectionStatLabel>
                            <SectionStatValue color={theme.colors.accent.danger}>
                              {formatCurrency(group.totalAmount)}
                            </SectionStatValue>
                          </SectionStat>
                        </SectionInfo>
                        <StatusBadge status={group.status}>
                          {group.status.charAt(0).toUpperCase() + group.status.slice(1)}
                        </StatusBadge>
                      </SectionHeader>
                      <CardBody style={{ padding: 0 }}>
                        <Table>
                          <Table.Header>
                            <Table.Row>
                              <Table.HeaderCell style={{ width: '50px' }}> </Table.HeaderCell>
                              <Table.HeaderCell>Date</Table.HeaderCell>
                              <Table.HeaderCell>Category</Table.HeaderCell>
                              <Table.HeaderCell>Description</Table.HeaderCell>
                              <Table.HeaderCell>Vendor</Table.HeaderCell>
                              <Table.HeaderCell>Payment Method</Table.HeaderCell>
                              <Table.HeaderCell style={{ textAlign: 'right' }}>Amount</Table.HeaderCell>
                              {canEditPettyCash && <Table.HeaderCell>Actions</Table.HeaderCell>}
                            </Table.Row>
                          </Table.Header>
                          <Table.Body>
                            {group.expenses.map((expense) => (
                              <Table.Row key={expense.id}>
                                <Table.Cell>
                                  <input
                                    type="checkbox"
                                    checked={selectedExpenseIds.includes(expense.id)}
                                    onChange={() => handleToggleSelect(expense.id)}
                                  />
                                </Table.Cell>
                                <Table.Cell>{formatDate(expense.date)}</Table.Cell>
                                <Table.Cell>{expense.category || '—'}</Table.Cell>
                                <Table.Cell>{expense.description || '—'}</Table.Cell>
                                <Table.Cell>{expense.vendor || '—'}</Table.Cell>
                                <Table.Cell>{expense.paymentMethod || '—'}</Table.Cell>
                                <Table.Cell style={{ textAlign: 'right' }}>
                                  <AmountCell>{formatCurrency(expense.amount)}</AmountCell>
                                </Table.Cell>
                                {canEditPettyCash && (
                                  <Table.Cell>
                                    <div style={{ display: 'flex', gap: theme.spacing[2] }}>
                                      <ActionButton
                                        onClick={() => handleEditCheckedExpense(expense)}
                                        title="Edit"
                                      >
                                        <FiEdit size={18} />
                                      </ActionButton>
                                      <button
                                        onClick={() => handleResubmitChecked(expense.id)}
                                        title="Resubmit"
                                        style={{
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          padding: theme.spacing[2],
                                          border: 'none',
                                          borderRadius: theme.borderRadius.md,
                                          backgroundColor: 'transparent',
                                          color: theme.colors.primary[600],
                                          cursor: 'pointer',
                                          transition: 'all 0.2s ease',
                                        }}
                                        onMouseEnter={(e) => {
                                          e.currentTarget.style.backgroundColor = theme.colors.primary[50];
                                          e.currentTarget.style.color = theme.colors.primary[700];
                                        }}
                                        onMouseLeave={(e) => {
                                          e.currentTarget.style.backgroundColor = 'transparent';
                                          e.currentTarget.style.color = theme.colors.primary[600];
                                        }}
                                      >
                                        <FiSend size={18} />
                                      </button>
                                    </div>
                                  </Table.Cell>
                                )}
                              </Table.Row>
                            ))}
                          </Table.Body>
                        </Table>
                      </CardBody>
                    </SubmissionSection>
                  );
                })}

                {totalPages > 1 && (
                  <div style={{ display: 'flex', justifyContent: 'center', marginTop: theme.spacing[6] }}>
                    <div style={{ display: 'flex', gap: theme.spacing[2] }}>
                      <Button
                        variant="secondary"
                        size="sm"
                        disabled={currentPage === 1}
                        onClick={() => handlePageChange(currentPage - 1)}
                      >
                        Previous
                      </Button>
                      <div style={{ display: 'flex', alignItems: 'center', padding: `0 ${theme.spacing[4]}` }}>
                        Page {currentPage} of {totalPages}
                      </div>
                      <Button
                        variant="secondary"
                        size="sm"
                        disabled={currentPage === totalPages}
                        onClick={() => handlePageChange(currentPage + 1)}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <Card>
                <CardBody>
                  <EmptyState>
                    <p>No checked expenses.</p>
                  </EmptyState>
                </CardBody>
              </Card>
            )}
          </TabContent>
        </TabsContainer>

        {/* Add Expense Modal */}
        <Modal
          isOpen={showExpenseModal}
          onClose={() => {
            setShowExpenseModal(false);
            setFormData({
              date: new Date().toISOString().split('T')[0],
              amount: '',
              category: '',
              description: '',
              vendor: '',
              paymentMethod: 'cash',
              ref: '',
            });
          }}
          title="Add Expense"
          size="lg"
          footer={
            <>
              <Button
                variant="secondary"
                onClick={() => {
                  setShowExpenseModal(false);
                  setFormData({
                    date: new Date().toISOString().split('T')[0],
                    amount: '',
                    category: '',
                    description: '',
                    vendor: '',
                    paymentMethod: 'cash',
                    ref: '',
                  });
                }}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  const form = document.getElementById('expense-form') as HTMLFormElement;
                  if (form) {
                    form.requestSubmit();
                  }
                }}
                loading={saving}
                disabled={saving}
              >
                Add Expense
              </Button>
            </>
          }
        >
          <form id="expense-form" onSubmit={handleSubmit}>
            <FormRow>
              <FormGroup>
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  required
                  dark
                />
              </FormGroup>
              <FormGroup>
                <Label>Amount *</Label>
                <Input
                  type="text"
                  value={formData.amount}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '' || /^\d*\.?\d*$/.test(value)) {
                      setFormData({ ...formData, amount: value });
                    }
                  }}
                  placeholder="0.00"
                  required
                  dark
                />
              </FormGroup>
            </FormRow>

            <FormRow>
              <FormGroup>
                <Label>Category *</Label>
                <Select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  required
                >
                  <option value="">Select Category</option>
                  {expenseCategories.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </Select>
              </FormGroup>
              <FormGroup>
                <Label>Payment Method</Label>
                <Select
                  value={formData.paymentMethod}
                  onChange={(e) => setFormData({ ...formData, paymentMethod: e.target.value })}
                >
                  <option value="cash">Cash</option>
                  <option value="bank">Bank Transfer</option>
                  <option value="card">Card</option>
                  <option value="cheque">Cheque</option>
                </Select>
              </FormGroup>
            </FormRow>

            <FormGroup>
              <Label>Description *</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Enter expense description"
                required
                dark
              />
            </FormGroup>

            <FormRow>
              <FormGroup>
                <Label>Vendor</Label>
                <Input
                  value={formData.vendor}
                  onChange={(e) => setFormData({ ...formData, vendor: e.target.value })}
                  placeholder="Vendor name"
                  dark
                />
              </FormGroup>
              <FormGroup>
                <Label>Reference</Label>
                <Input
                  value={formData.ref}
                  onChange={(e) => setFormData({ ...formData, ref: e.target.value })}
                  placeholder="Reference number"
                  dark
                />
              </FormGroup>
            </FormRow>
          </form>
        </Modal>


        {/* Delete Confirmation Modal */}
        <Modal
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
            setExpenseToDelete(null);
          }}
          title="Delete Expense"
          footer={
            <>
              <Button
                variant="secondary"
                onClick={() => {
                  setShowDeleteModal(false);
                  setExpenseToDelete(null);
                }}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={confirmDelete}
              >
                Delete
              </Button>
            </>
          }
        >
          <p>Are you sure you want to delete this expense? This action cannot be undone.</p>
        </Modal>

        {/* Submission Detail Modal */}
        <Modal
          isOpen={showSubmissionDetailModal}
          onClose={() => {
            setShowSubmissionDetailModal(false);
            setSelectedSubmission(null);
          }}
          title="Submission Details"
          size="xl"
          footer={
            <Button
              variant="secondary"
              onClick={() => {
                setShowSubmissionDetailModal(false);
                setSelectedSubmission(null);
              }}
            >
              Close
            </Button>
          }
        >
          {selectedSubmission && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing[4] }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: theme.spacing[4] }}>
                <div>
                  <Label>Submitted Date</Label>
                  <div style={{ marginTop: theme.spacing[1] }}>
                    {formatDate(selectedSubmission.submittedAt)}
                  </div>
                </div>
                <div>
                  <Label>Submission Period</Label>
                  <div style={{ marginTop: theme.spacing[1] }}>
                    {selectedSubmission.submissionPeriod
                      ? selectedSubmission.submissionPeriod.charAt(0).toUpperCase() + selectedSubmission.submissionPeriod.slice(1)
                      : '—'}
                  </div>
                </div>
                <div>
                  <Label>Number of Expenses</Label>
                  <div style={{ marginTop: theme.spacing[1] }}>
                    {selectedSubmission.expenses.length}
                  </div>
                </div>
                <div>
                  <Label>Total Amount</Label>
                  <div style={{ marginTop: theme.spacing[1], fontWeight: theme.typography.fontWeight.bold, color: theme.colors.accent.danger }}>
                    {formatCurrency(selectedSubmission.totalAmount)}
                  </div>
                </div>
                <div>
                  <Label>Status</Label>
                  <div style={{ marginTop: theme.spacing[1] }}>
                    <StatusBadge status={selectedSubmission.status}>
                      {selectedSubmission.status.charAt(0).toUpperCase() + selectedSubmission.status.slice(1)}
                    </StatusBadge>
                  </div>
                </div>
              </div>

              <div>
                <Label style={{ marginBottom: theme.spacing[2], display: 'block' }}>
                  Expenses ({selectedSubmission.expenses.length})
                </Label>
                <div style={{ maxHeight: '500px', overflowY: 'auto', overflowX: 'visible', width: '100%' }}>
                  <div style={{ width: '100%', overflowX: 'visible' }}>
                    <Table>
                      <Table.Header>
                        <Table.Row>
                          <Table.HeaderCell style={{ minWidth: '100px' }}>Date</Table.HeaderCell>
                          <Table.HeaderCell style={{ minWidth: '120px' }}>Category</Table.HeaderCell>
                          <Table.HeaderCell style={{ minWidth: '200px' }}>Description</Table.HeaderCell>
                          <Table.HeaderCell style={{ minWidth: '120px' }}>Vendor</Table.HeaderCell>
                          <Table.HeaderCell style={{ minWidth: '130px' }}>Payment Method</Table.HeaderCell>
                          <Table.HeaderCell style={{ textAlign: 'right', minWidth: '100px' }}>Amount</Table.HeaderCell>
                          <Table.HeaderCell style={{ minWidth: '100px' }}>Status</Table.HeaderCell>
                        </Table.Row>
                      </Table.Header>
                      <Table.Body>
                        {selectedSubmission.expenses.map((expense: PettyCashExpense) => (
                          <Table.Row key={expense.id}>
                            <Table.Cell>{formatDate(expense.date)}</Table.Cell>
                            <Table.Cell>{expense.category || '—'}</Table.Cell>
                            <Table.Cell>{expense.description || '—'}</Table.Cell>
                            <Table.Cell>{expense.vendor || '—'}</Table.Cell>
                            <Table.Cell>{expense.paymentMethod || '—'}</Table.Cell>
                            <Table.Cell style={{ textAlign: 'right' }}>
                              <AmountCell>{formatCurrency(expense.amount)}</AmountCell>
                            </Table.Cell>
                            <Table.Cell>
                              <StatusBadge status={expense.submissionStatus}>
                                {expense.submissionStatus.charAt(0).toUpperCase() + expense.submissionStatus.slice(1)}
                              </StatusBadge>
                            </Table.Cell>
                          </Table.Row>
                        ))}
                      </Table.Body>
                    </Table>
                  </div>
                </div>
              </div>
            </div>
          )}
        </Modal>

        {/* Edit Checked Expense Modal */}
        <Modal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setExpenseToEdit(null);
          }}
          title="Edit Expense"
          size="lg"
          footer={
            <>
              <Button
                variant="secondary"
                onClick={() => {
                  setShowEditModal(false);
                  setExpenseToEdit(null);
                }}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                onClick={handleUpdateCheckedExpense}
                loading={saving}
                disabled={saving}
              >
                Update Expense
              </Button>
            </>
          }
        >
          <form id="edit-expense-form">
            <FormRow>
              <FormGroup>
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={editFormData.date}
                  onChange={(e) => setEditFormData({ ...editFormData, date: e.target.value })}
                  required
                  dark
                />
              </FormGroup>
              <FormGroup>
                <Label>Amount *</Label>
                <Input
                  type="text"
                  value={editFormData.amount}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '' || /^\d*\.?\d*$/.test(value)) {
                      setEditFormData({ ...editFormData, amount: value });
                    }
                  }}
                  placeholder="0.00"
                  required
                  dark
                />
              </FormGroup>
            </FormRow>

            <FormRow>
              <FormGroup>
                <Label>Category *</Label>
                <Select
                  value={editFormData.category}
                  onChange={(e) => setEditFormData({ ...editFormData, category: e.target.value })}
                  required
                >
                  <option value="">Select Category</option>
                  {expenseCategories.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </Select>
              </FormGroup>
              <FormGroup>
                <Label>Payment Method</Label>
                <Select
                  value={editFormData.paymentMethod}
                  onChange={(e) => setEditFormData({ ...editFormData, paymentMethod: e.target.value })}
                >
                  <option value="cash">Cash</option>
                  <option value="bank">Bank Transfer</option>
                  <option value="card">Card</option>
                  <option value="cheque">Cheque</option>
                </Select>
              </FormGroup>
            </FormRow>

            <FormGroup>
              <Label>Description *</Label>
              <Input
                value={editFormData.description}
                onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                placeholder="Enter expense description"
                required
                dark
              />
            </FormGroup>

            <FormRow>
              <FormGroup>
                <Label>Vendor</Label>
                <Input
                  value={editFormData.vendor}
                  onChange={(e) => setEditFormData({ ...editFormData, vendor: e.target.value })}
                  placeholder="Vendor name"
                  dark
                />
              </FormGroup>
              <FormGroup>
                <Label>Reference</Label>
                <Input
                  value={editFormData.ref}
                  onChange={(e) => setEditFormData({ ...editFormData, ref: e.target.value })}
                  placeholder="Reference number"
                  dark
                />
              </FormGroup>
            </FormRow>
          </form>
        </Modal>
      </ScrollableContainer>
    </DashboardLayout>
  );
};

export default PettyCashBookPage;
